import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import { Selectors } from "../../Everou/_reducers/app.reducer";
import { PMSLocationsActions } from "../_actions/PMSLocationsActions";
import { CapsuleButton, CustomCard } from "../../Common/_components";
import { CaptionText, CustomSearchBar } from "../../Everou/_components";
import { deviceIconForType, Icons } from "../../Everou/_assets";
import { DeviceHelper } from "../../Everou/_helpers";
import {
    PublicDeviceMode,
    PublicDeviceOverride,
    areOverridesEqual,
    countMode,
    mapToOverrides,
    nextMode,
    overridesToMap,
} from "../_helpers/PublicDeviceOverridesHelper";

type Mode = PublicDeviceMode;
type Override = PublicDeviceOverride;

export function PMSLocationPublicLinkDevices() {

    // LIBRARIES
    const { t } = useTranslation();
    const dispatch = useDispatch();

    // GLOBAL STATE
    const locationUid = useSelector(Selectors.getPMSLocationSelectedUid);
    const devices = useSelector(Selectors.getPMSLocationDevices);
    const rooms = useSelector(Selectors.getPMSLocationRooms);
    const locationInfo = useSelector(Selectors.getPMSLocationInfo);

    const initialOverrides: Override[] =
        locationInfo.publicDeviceOverrides ?? [];

    // LOCAL STATE
    const [overrides, setOverrides] = useState<Map<string, Mode>>(
        () => overridesToMap(initialOverrides),
    );

    useEffect(() => {
        setOverrides(overridesToMap(locationInfo.publicDeviceOverrides ?? []));
    }, [locationInfo.publicDeviceOverrides]);

    const initialMap = useMemo(
        () => overridesToMap(initialOverrides),
        [initialOverrides],
    );

    const isDirty = useMemo(
        () => !areOverridesEqual(overrides, initialMap),
        [overrides, initialMap],
    );

    const [searchTerm, setSearchTerm] = useState("");

    const eligibleDevices = useMemo(
        () => {
            const allDevices = devices ?? [];
            const eligibleUids = new Set(
                allDevices
                    .filter(d => d.isPublicEligible)
                    .map(d => d.uid),
            );
            return allDevices.filter(
                d => d.isPublicEligible || eligibleUids.has(d.kit_uid),
            );
        },
        [devices],
    );

    const groupedDevices = useMemo(
        () => {
            if (!searchTerm.trim()) return eligibleDevices;
            const term = searchTerm.trim().toLowerCase();
            return eligibleDevices.filter(d =>
                d.description?.toLowerCase().includes(term),
            );
        },
        [eligibleDevices, searchTerm],
    );

    const hiddenCount = useMemo(
        () => countMode(overrides, "hidden"),
        [overrides],
    );

    const codeOnlyCount = useMemo(
        () => countMode(overrides, "code_only"),
        [overrides],
    );

    // ACTION HANDLERS
    function cycleDevice(device) {
        const current = overrides.get(device.uid);
        const next = nextMode(current, device.supportsCodeOnlyMode);

        const newMap = new Map(overrides);
        if (next === undefined) {
            newMap.delete(device.uid);
        } else {
            newMap.set(device.uid, next);
        }
        setOverrides(newMap);
    }

    function handleSave() {
        dispatch(
            PMSLocationsActions.updatePublicDeviceOverrides(
                mapToOverrides(overrides),
                locationUid,
            ),
        );
    }

    function handleDiscard() {
        setOverrides(overridesToMap(initialOverrides));
    }

    // VIEW
    if (!eligibleDevices.length) {
        return (
            <CaptionText
                style={{ height: "90%" }}
                className="d-flex align-items-center justify-content-center"
            >
                {t("mod_pms_location_public_link_devices_empty")}
            </CaptionText>
        );
    }

    return (
        <div className="w-100">
            {header()}
            {roomSections()}
        </div>
    );

    function header() {
        return (
            <div className="mb-3 px-2">
                <div className="d-flex align-items-center c-bg-grey-250 c-rounded-big p-3 mb-2">
                    <img
                        src={Icons.info}
                        alt="info"
                        className="mr-2 flex-shrink-0"
                        style={{ width: "24px", height: "24px" }}
                    />
                    <span>
                        {t("mod_pms_location_public_link_devices_info_tooltip")}
                    </span>
                </div>
                {eligibleDevices.length > 10 && (
                    <CustomSearchBar
                        onChange={e => setSearchTerm(e.target.value)}
                        value={searchTerm}
                        placeholder={t("global_search_placeholder")}
                    />
                )}
                <div className="d-flex align-items-center justify-content-end flex-wrap mt-2">
                    <div className="d-flex align-items-center flex-shrink-0">
                        <small className="text-secondary mr-3 text-right">
                            {summaryLabel()}
                        </small>
                        {isDirty && (
                            <>
                                <CapsuleButton
                                    text={t("global_cancel")}
                                    style="DANGER"
                                    size="SMALL"
                                    onClick={handleDiscard}
                                />
                                <div className="ml-2" />
                                <CapsuleButton
                                    text={t("global_save")}
                                    style="PRIMARY"
                                    fillStyle="FILLED"
                                    size="SMALL"
                                    onClick={handleSave}
                                />
                            </>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    function summaryLabel() {
        const parts: string[] = [];
        if (hiddenCount > 0) {
            parts.push(
                t("mod_pms_location_public_link_devices_hidden_count", {
                    count: hiddenCount,
                }),
            );
        }
        if (codeOnlyCount > 0) {
            parts.push(
                t("mod_pms_location_public_link_devices_code_only_count", {
                    count: codeOnlyCount,
                }),
            );
        }
        return parts.length ? parts.join(" · ") : null;
    }

    function roomSections() {
        return rooms
            .slice()
            .sort((a, b) => (a.devicesAmount > b.devicesAmount ? -1 : 1))
            .map(room => {
                const roomDevices = groupedDevices.filter(
                    d => d.room_uid === room.uid,
                );
                if (!roomDevices.length) return null;
                return (
                    <div
                        key={room.uid}
                        className="p-3 mb-3 c-bg-grey-245 c-rounded-big"
                    >
                        <div className="px-2 mb-3">
                            <h3 className="c-line-height-1 mb-0">{room.name}</h3>
                        </div>
                        <div className="c-default-grid">
                            {roomDevices
                                .slice()
                                .sort((a, b) =>
                                    a.description.localeCompare(b.description),
                                )
                                .map(deviceCard)}
                        </div>
                    </div>
                );
            });
    }

    function deviceCard(device) {
        const mode = overrides.get(device.uid);
        const isModified = mode !== undefined;
        const opacity =
            mode === "hidden" ? 0.55 : mode === "code_only" ? 0.85 : 1;
        return (
            <div
                key={device.uid}
                onClick={() => cycleDevice(device)}
                className={`cp c-rounded-1rem bg-white c-bd-220 c-hover-box-shadow c-transition-fast d-flex align-items-center ${
                    isModified ? "c-bd-left-primary-large c-bd-primary" : ""
                }`}
                style={{ opacity, padding: "8px 12px" }}
            >
                <img
                    className="c-img-1-25rem"
                    src={deviceIconForType(device.type)}
                    alt="device type icon"
                />
                <div className="d-flex justify-content-between w-100 ml-2 align-items-center">
                    <small className="c-line-height-1rem mr-2">
                        {device.description}
                    </small>
                    {modeLabel(mode)}
                </div>
            </div>
        );
    }

    function modeLabel(mode: Mode | undefined) {
        const baseStyle = {
            fontSize: "10px",
            whiteSpace: "nowrap" as const,
        };
        if (mode === "hidden") {
            return (
                <small
                    className="text-danger c-font-weight-medium text-uppercase"
                    style={baseStyle}
                >
                    {t("mod_pms_location_public_link_devices_hidden")}
                </small>
            );
        }
        if (mode === "code_only") {
            return (
                <small
                    className="text-info c-font-weight-medium text-uppercase"
                    style={baseStyle}
                >
                    {t("mod_pms_location_public_link_devices_code_only")}
                </small>
            );
        }
        return (
            <small className="text-secondary text-uppercase" style={baseStyle}>
                {t("mod_pms_location_public_link_devices_visible")}
            </small>
        );
    }
}

